import React, { useState, useEffect } from "react";
import { Box, Drawer, Typography, Button, Slider } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { RotateCcw, ListFilter, Logs, CircleCheck } from "lucide-react";
import CustomMultiSelect from "../Layout/DataContract/Viewkpi/CustomMultiSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PRIMARY_COLOR = "#bb1ccc";

/**
 * Field type definitions:
 *
 * multiselect: { type, key, label, options: [{label, value}], disabled?, onChange?, onMenuClose? }
 *   - onChange(selectedValues[], currentFilters) → partialUpdate | void
 *   - onMenuClose(currentFilters) → partialUpdate | void  (called when dropdown closes)
 *
 * range: { type, key, label, min, max }
 *
 * toggle: { type, key, label, onChange? }
 *   - onChange(newBool, currentFilters) → partialUpdate | void
 *
 * toggle-group: { type, key, label?, options: [{label, value}], onChange? }
 *   - onChange(selectedValue, currentFilters) → partialUpdate | void
 *
 * custom: { type, key, render(localFilters, setLocalFilters) → ReactNode }
 *
 * Section shape: { title: string, fields: Field[] }
 */
const ReusableFilterDrawer = ({
  open,
  onClose,
  onApply,
  onReset,
  values = {},
  sections = [],
  flat = false,
  defaultExpandedSection,
  title = "Advanced Filters",
  subtitle = "Refine your search criteria",
}) => {
  const computeDefaults = () => {
    const defaults = {};
    sections.forEach((section) => {
      (section.fields || []).forEach((field) => {
        if (field.defaultValue !== undefined) {
          defaults[field.key] = field.defaultValue;
        } else if (field.type === "range") {
          defaults[field.key] = [field.min ?? 0, field.max ?? 100];
        } else if (field.type === "toggle") {
          defaults[field.key] = false;
        } else if (field.type === "toggle-group") {
          defaults[field.key] = field.options?.[0]?.value ?? "";
        } else if (field.type === "date") {
          defaults[field.key] = null;
        } else {
          defaults[field.key] = [];
        }
      });
    });
    return defaults;
  };

  const [localFilters, setLocalFilters] = useState(computeDefaults);
  const [expandedSection, setExpandedSection] = useState(
    defaultExpandedSection ?? sections[0]?.title,
  );

  useEffect(() => {
    if (!open) return;
    const defaults = computeDefaults();
    const merged = {};
    Object.keys(defaults).forEach((key) => {
      merged[key] = values[key] !== undefined ? values[key] : defaults[key];
    });
    setLocalFilters(merged);
    setExpandedSection(defaultExpandedSection ?? sections[0]?.title);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => {
    onApply?.(localFilters);
    onClose();
  };

  const handleReset = () => {
    setLocalFilters(computeDefaults());
    onReset?.();
  };

  const toggleSection = (sectionTitle) => {
    setExpandedSection((prev) => (prev === sectionTitle ? null : sectionTitle));
  };

  const renderField = (field) => {
    switch (field.type) {
      case "multiselect": {
        if (!field.options || field.options.length === 0) return null;
        const currentValue = field.options.filter((o) =>
          (localFilters[field.key] || []).includes(o.value),
        );
        return (
          <CustomMultiSelect
            label={field.label}
            options={field.options}
            value={currentValue}
            onChange={(_, v) => {
              const selectedValues = v ? v.map((opt) => opt.value) : [];
              if (field.onChange) {
                const partial = field.onChange(selectedValues, localFilters);
                setLocalFilters((prev) => ({
                  ...prev,
                  ...(partial || { [field.key]: selectedValues }),
                }));
              } else {
                setLocalFilters((prev) => ({ ...prev, [field.key]: selectedValues }));
              }
            }}
            onClose={
              field.onMenuClose
                ? () => {
                    const partial = field.onMenuClose(localFilters);
                    if (partial && Object.keys(partial).length > 0) {
                      setLocalFilters((prev) => ({ ...prev, ...partial }));
                    }
                  }
                : undefined
            }
            placeholder="Select"
            disabled={field.disabled}
            noOptionsText={`No ${field.label?.toLowerCase()} found`}
          />
        );
      }

      case "range": {
        const min = field.min ?? 0;
        const max = field.max ?? 100;
        const value = localFilters[field.key] || [min, max];
        return (
          <Box sx={{ mb: 3 }}>
            <Typography fontSize={12} fontWeight={600} mb={1} color="#374151">
              {field.label}
            </Typography>
            <Slider
              value={value}
              onChange={(_, v) =>
                setLocalFilters((prev) => ({ ...prev, [field.key]: v }))
              }
              valueLabelDisplay="auto"
              min={min}
              max={max}
              sx={{
                color: PRIMARY_COLOR,
                "& .MuiSlider-thumb": { backgroundColor: PRIMARY_COLOR },
                "& .MuiSlider-track": { backgroundColor: PRIMARY_COLOR },
              }}
            />
            <Typography fontSize={10} color="text.secondary">
              Range: {value.join(" - ")}
            </Typography>
          </Box>
        );
      }

      case "toggle": {
        const isOn = !!localFilters[field.key];
        return (
          <Box
            sx={{
              mb: 2,
              p: 1,
              backgroundColor: "#F9FAFB",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography fontSize={12} fontWeight={600} color="#374151">
              {field.label}
            </Typography>
            <Box
              onClick={() => {
                const newVal = !isOn;
                if (field.onChange) {
                  const partial = field.onChange(newVal, localFilters);
                  setLocalFilters((prev) => ({
                    ...prev,
                    ...(partial || { [field.key]: newVal }),
                  }));
                } else {
                  setLocalFilters((prev) => ({ ...prev, [field.key]: newVal }));
                }
              }}
              sx={{
                width: 44,
                height: 24,
                borderRadius: "12px",
                backgroundColor: isOn ? PRIMARY_COLOR : "#D1D5DB",
                position: "relative",
                cursor: "pointer",
                transition: "background-color 0.2s",
                flexShrink: 0,
                "&:hover": { backgroundColor: isOn ? "#000080" : "#9CA3AF" },
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  position: "absolute",
                  top: 3,
                  left: isOn ? 23 : 3,
                  transition: "left 0.2s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              />
            </Box>
          </Box>
        );
      }

      case "toggle-group": {
        const current = localFilters[field.key] ?? field.options?.[0]?.value;
        return (
          <Box sx={{ mb: 2 }}>
            {field.label && (
              <Typography fontSize={12} fontWeight={600} mb={1} color="#374151">
                {field.label}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                p: 0.5,
                backgroundColor: "#F3F4F6",
                borderRadius: "8px",
              }}
            >
              {(field.options || []).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    if (field.onChange) {
                      const partial = field.onChange(opt.value, localFilters);
                      setLocalFilters((prev) => ({
                        ...prev,
                        ...(partial || { [field.key]: opt.value }),
                      }));
                    } else {
                      setLocalFilters((prev) => ({ ...prev, [field.key]: opt.value }));
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    backgroundColor: current === opt.value ? "#fff" : "transparent",
                    color: current === opt.value ? PRIMARY_COLOR : "#6B7280",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 600,
                    transition: "all 0.2s",
                    boxShadow:
                      current === opt.value ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </Box>
          </Box>
        );
      }

      case "date": {
        const dateStr = localFilters[field.key] || "";
        const dateObj = dateStr ? new Date(dateStr) : null;

        const formatLabelDate = (date) =>
          date
            ? date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "";

        return (
          <Box sx={{ mb: 2 }}>
            <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
              {field.label}
              {dateObj && (
                <span style={{ color: "#6B7280", fontWeight: 400 }}>
                  {" "}({formatLabelDate(dateObj)})
                </span>
              )}
            </label>
            <DatePicker
              selected={dateObj}
              onChange={(date) => {
                const formatted = date ? date.toISOString().split("T")[0] : null;
                setLocalFilters((prev) => ({ ...prev, [field.key]: formatted }));
              }}
              placeholderText="Select date"
              dateFormat="MM/dd/yyyy"
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={150}
              wrapperClassName="w-full"
              className="h-[44px] w-full rounded-md border border-gray-300 px-3 text-sm focus:border-blue-600 focus:outline-none"
            />
          </Box>
        );
      }

      case "custom": {
        return field.render ? field.render(localFilters, setLocalFilters) : null;
      }

      default:
        return null;
    }
  };

  const renderAccordion = (section) => {
    const renderedFields = (section.fields || []).map((field, idx) => {
      const rendered = renderField(field);
      if (!rendered) return null;
      return <React.Fragment key={field.key ?? idx}>{rendered}</React.Fragment>;
    });

    const hasContent = renderedFields.some((f) => f !== null);
    if (!hasContent) return null;

    const isOpen = expandedSection === section.title;

    return (
      <Box
        key={section.title}
        sx={{ mb: 1, border: "1px solid #E5E7EB", borderRadius: "10px" }}
      >
        <Box
          onClick={() => toggleSection(section.title)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 2.5,
            cursor: "pointer",
            userSelect: "none",
            borderRadius: "5px",
            ":hover": { backgroundColor: "#f5f3ff" },
          }}
        >
          <div className="text-[13px] font-bold flex items-center gap-2">
            <div
              className={
                isOpen
                  ? "bg-[#6e72d9] text-white h-[32px] w-[32px] flex items-center justify-center rounded-lg"
                  : "text-[#9596e1]"
              }
            >
              <Logs className="h-5" />
            </div>
            <p>{section.title}</p>
          </div>
          <ChevronRightIcon
            sx={{
              fontSize: 18,
              color: "#6B7280",
              transition: "transform 0.2s ease",
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        </Box>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              pt: 2,
              px: 2,
              backgroundColor: "#fbfcff",
              maxHeight: "280px",
              overflow: "auto",
            }}
          >
            {renderedFields}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 380,
          background: "#f9fafb",
          height: "calc(100% - 66px)",
          display: "flex",
          flexDirection: "column",
          marginTop: "66px",
        }}
      >
        {/* Header */}
        <div className="px-3 py-5 bg-gradient-to-r from-[#bb1ccc] to-[#bb1ccc]">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div className="flex items-center gap-3 text-white">
              <div className="bg-[#5558E3] text-white px-2 py-1 rounded-md hover:animate-spin">
                <ListFilter className="h-8" />
              </div>
              <div className="flex flex-col">
                <p className="text-lg font-bold text-white">{title}</p>
                <span>{subtitle}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </Box>
        </div>

        {/* Sections */}
        <Box sx={{ flex: 1, overflowY: "auto", mb: 2, p: 3 }}>
          {flat
            ? sections
                .flatMap((s) => s.fields || [])
                .map((field, idx) => (
                  <React.Fragment key={field.key ?? idx}>
                    {renderField(field)}
                  </React.Fragment>
                ))
            : sections.map((section) => renderAccordion(section))}
        </Box>

        {/* Footer */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, px: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleReset}
            sx={{
              borderColor: "#D1D5DB",
              color: "#374151",
              "&:hover": { borderColor: "#9CA3AF", backgroundColor: "#F3F4F6" },
              textTransform: "none",
              py: 1.2,
              borderRadius: "8px",
              fontSize: "10px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <RotateCcw style={{ width: "14px", height: "14px" }} />
            Reset
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApply}
            sx={{
              backgroundColor: "#bb1ccc",
              textTransform: "none",
              py: 1.2,
              borderRadius: "8px",
              fontSize: "10px",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#4f52d1" },
              gap: 1,
            }}
          >
            <CircleCheck style={{ width: "14px", height: "14px" }} />
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ReusableFilterDrawer;
